import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_URL } from '../../../api/api';

const API_state = `${API_URL}/state`;
const API_branch = `${API_URL}/branches`;

const AddBranchLayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [statesData, setStatesData] = useState([]);
  const [districts, setDistricts] = useState([]);

  const [formData, setFormData] = useState({
    branchname: '',
    branchcode: '',
    openingdate: '',
    branchmanager: '',
    contactnumber: '',
    contactemail: '',
    landline: '',
    password: '',
    collectionday: '',
    addressline: '',
    addressline2: '',
    state: '',
    district: '',
    city: '',
    pincode: '',
    country: 'India',
    ifsccode: ''
  });

  const [errors, setErrors] = useState({});

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    fetchStateData();
    if (id) fetchBranchData();
  }, [id]);

  // Fetch states list
  const fetchStateData = async () => {
    try {
      const response = await axios.get(API_state, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatesData(response.data);
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  };

  // Fetch branch for editing
  const fetchBranchData = async () => {
    try {
      const response = await axios.get(`${API_branch}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const branch = response.data;
      setFormData(prev => ({
        ...prev,
        ...branch,
        state: branch.state || '',
        district: branch.district || '',
      }));
      const selectedState = statesData.find(s => s.state_name === branch.state);
      setDistricts(selectedState?.districts || []);
    } catch (error) {
      console.error("Error loading branch data:", error);
      Swal.fire("Failed to load branch data", "", "error");
    }
  };

  // Validate form fields
  const validateForm = () => {
    let tempErrors = {};
    if (!formData.branchname) tempErrors.branchname = "Branch Name is required.";
    if (!formData.openingdate) tempErrors.openingdate = "Opening Date is required.";
    if (!formData.branchmanager) tempErrors.branchmanager = "Branch Manager is required.";
    if (!formData.contactnumber) tempErrors.contactnumber = "Contact Number is required.";
    if (!formData.contactemail) tempErrors.contactemail = "Contact Email is required.";
    if (!formData.state) tempErrors.state = "State is required.";
    if (!formData.district) tempErrors.district = "District is required.";
    if (!formData.city) tempErrors.city = "City is required.";
    if (!formData.pincode) tempErrors.pincode = "Pincode is required.";
    if (!formData.ifsccode) tempErrors.ifsccode = "IFSC Code is required.";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  // Generate branch code like: first 3 letters of district + first 2 letters of city + padded number
  // You may want to get current max branch id or count from backend to increment
  const generateBranchCode = async () => {
    try {
      // Get last branch code suffix number from API or count
      const response = await axios.get(`${API_branch}/max-code`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const lastNumber = response.data.lastNumber || 0; // example: { lastNumber: 25 }

      const districtPart = formData.district ? formData.district.substring(0, 3).toUpperCase() : 'XXX';
      const cityPart = formData.city ? formData.city.substring(0, 2).toUpperCase() : 'XX';
      const numberPart = String(lastNumber + 1).padStart(4, '0'); // e.g., 0026

      return `${districtPart}${cityPart}${numberPart}`;
    } catch (error) {
      console.error("Error generating branch code:", error);
      // fallback code
      return `BR${Date.now()}`;
    }
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'state') {
      const selected = statesData.find(s => s.state_name === value);
      setDistricts(selected?.districts || []);
      setFormData(prev => ({ ...prev, district: '' }));
    }

    // Auto-generate branchcode if district or city changes
    if (name === 'district' || name === 'city') {
      if (formData.district && formData.city) {
        const code = await generateBranchCode();
        setFormData(prev => ({ ...prev, branchcode: code }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      // Ensure branchcode is generated if not already
      if (!formData.branchcode) {
        const code = await generateBranchCode();
        setFormData(prev => ({ ...prev, branchcode: code }));
      }

      const method = id ? 'put' : 'post';
      const url = id ? `${API_branch}/${id}` : API_branch;

      await axios[method](url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      Swal.fire('Success', `Branch ${id ? 'updated' : 'created'} successfully!`, 'success');
      navigate('/managebranch');
    } catch (error) {
      console.error("Submit failed:", error);
      Swal.fire('Error', 'Failed to save branch. Please try again.', 'error');
    }
  };

  const renderInput = (label, name, type = 'text', placeholder = '', required = true) => (
    <div className="mb-3 row" key={name}>
      <label className="col-sm-4 col-form-label">{label}</label>
      <div className="col-sm-8">
        <input
          type={type}
          className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
          name={name}
          value={formData[name] || ''}
          placeholder={placeholder}
          onChange={handleInputChange}
          required={required}
        />
        {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
      </div>
    </div>
  );

  const renderSelect = (label, name, options = [], required = true, valueKey = '', labelKey = '') => (
    <div className="mb-3 row" key={name}>
      <label className="col-sm-4 col-form-label">{label}</label>
      <div className="col-sm-8">
        <select
          className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
          name={name}
          value={formData[name] || ''}
          onChange={handleInputChange}
          required={required}
        >
          <option value="" disabled>Select {label}</option>
          {options.map((opt, idx) => {
            const value = valueKey ? opt[valueKey] : opt;
            const display = labelKey ? opt[labelKey] : opt;
            return (
              <option key={idx} value={value}>{display}</option>
            );
          })}
        </select>
        {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
      </div>
    </div>
  );

  return (
    <div className="row justify-content-center">
      <div className="col-lg-10">
        <div className="card shadow">
          <div className="card-header bg-info text-white">
            <h6 className="mb-0">{id ? 'Edit Branch' : 'Add Branch'}</h6>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit} noValidate>
              <div className="row">
                <div className="col-md-6">
                  {renderInput('Branch Name', 'branchname')}
                  {renderInput('Opening Date', 'openingdate', 'date')}
                  {renderInput('Manager', 'branchmanager')}
                  {renderInput('Contact Number', 'contactnumber', 'tel')}
                  {renderInput('Contact Email', 'contactemail', 'email')}
                  {renderInput('Landline No.', 'landline', 'text', 'Optional', false)}
                  {renderInput('Password', 'password', 'password', 'Optional', false)}
                  {renderSelect('Collection Day', 'collectionday', days, false)}
                </div>
                <div className="col-md-6">
                  {renderInput('Address Line 1', 'addressline')}
                  {renderInput('Address Line 2', 'addressline2', 'text', 'Optional', false)}
                  {renderSelect('State', 'state', statesData.map(s => s.state_name))}
                  {renderSelect('District', 'district', districts, true, 'district', 'district')}
                  {renderInput('City', 'city')}
                  {renderInput('Pincode', 'pincode', 'number')}
                  {renderSelect('Country', 'country', ['India'], false)}
                  {renderInput('IFSC Code', 'ifsccode')}
                </div>
              </div>

              <div className="text-start mt-4">
                <button type="submit" className="btn btn-danger me-2" style={{ width: '120px' }}>
                  {id ? 'Update' : 'Save'}
                </button>
                <button type="reset" className="btn btn-secondary" style={{ width: '120px' }} onClick={() => navigate('/branches')}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBranchLayer;
